import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import translationService from './translationService';

// Create the context
export const LanguageContext = createContext();

// Provider component to manage language state and translations
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('DE'); // Default language is German
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to change the language
  const changeLanguage = useCallback(async (newLanguage) => {
    if (newLanguage === language) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load translations for new language
      await translationService.loadTranslations(newLanguage);
      setLanguage(newLanguage);
      localStorage.setItem('selectedLanguage', newLanguage);
      
      // Trigger re-render by updating translations state
      setTranslations(prev => ({ ...prev, [newLanguage]: Date.now() }));
    } catch (err) {
      console.error('Failed to change language:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  // Translation function
  const t = useCallback((key, fallback = key) => {
    return translationService.getTranslation(key, language, fallback);
  }, [language, translations]);

  // Preload translations for multiple languages
  const preloadLanguages = useCallback(async (languages) => {
    const promises = languages.map(lang => translationService.loadTranslations(lang));
    try {
      await Promise.all(promises);
    } catch (err) {
      console.warn('Some translations failed to preload:', err);
    }
  }, []);

  // Load language from localStorage and initial translations on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      // Get language from URL parameter first, then localStorage, then default
      const urlLang = window.location.pathname.split('/')[1];
      const urlLanguageMap = { 'de': 'DE', 'en': 'EN', 'fr': 'FR', 'es': 'ES', 'it': 'IT' };
      const languageFromUrl = urlLanguageMap[urlLang?.toLowerCase()];
      
      const savedLanguage = languageFromUrl || localStorage.getItem('selectedLanguage') || 'DE';
      
      setIsLoading(true);
      try {
        await translationService.loadTranslations(savedLanguage);
        setLanguage(savedLanguage);
        setTranslations(prev => ({ ...prev, [savedLanguage]: Date.now() }));
        
        // Save to localStorage
        localStorage.setItem('selectedLanguage', savedLanguage);
        
        // Preload common languages for better UX
        const commonLanguages = ['DE', 'EN'];
        const languagesToPreload = commonLanguages.filter(lang => lang !== savedLanguage);
        if (languagesToPreload.length > 0) {
          // Don't await this - let it load in background
          preloadLanguages(languagesToPreload).catch(err => 
            console.warn('Background preloading failed:', err)
          );
        }
      } catch (err) {
        console.error('Failed to initialize language:', err);
        setError(err.message);
        // Set default language even if API fails
        setLanguage(savedLanguage);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, [preloadLanguages]);

  const value = {
    language,
    changeLanguage,
    t,
    isLoading,
    error,
    preloadLanguages,
    isTranslationLoaded: (locale) => translationService.isLoaded(locale),
    clearTranslationCache: (locale) => {
      translationService.clearCache(locale);
      setTranslations(prev => ({ ...prev, [locale]: undefined }));
    }
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for easier access to translation functions
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Custom hook to sync language with URL parameter
export const useUrlLanguageSync = () => {
  const { language, changeLanguage } = useTranslation();
  const { lang } = useParams();

  useEffect(() => {
    if (lang) {
      const urlLanguageMap = { 'de': 'DE', 'en': 'EN', 'fr': 'FR', 'es': 'ES', 'it': 'IT' };
      const languageFromUrl = urlLanguageMap[lang.toLowerCase()];
      
      if (languageFromUrl && languageFromUrl !== language) {
        changeLanguage(languageFromUrl);
      }
    }
  }, [lang, language, changeLanguage]);

  return { language, urlLang: lang };
};

// Export the context for backward compatibility
export default LanguageContext;
